import React from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenantSlug } from '../../lib/use-tenant-slug';
import { useCancelledOrders } from '../../lib/admin-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, formatRelativeTime } from '../../lib/utils';
import { XCircle, ShoppingBag } from 'lucide-react';
export function CancelledOrdersPage() {
  const slug = useTenantSlug();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  // Get tenant context for role-aware layout
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const {
    orders,
    loading: ordersLoading
  } = useCancelledOrders(restaurant?.id);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-3199">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-3200" />
      </div>;
  }
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-3201">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-3202">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-3203">
          <div data-id="element-3204">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-3205">
              <XCircle className="w-6 h-6 text-red-500" data-id="element-3206" />
              Pedidos Cancelados
            </h1>
            <p className="text-gray-500" data-id="element-3207">
              Historial de pedidos cancelados ({orders.length})
            </p>
          </div>
        </div>

        <Card data-id="element-3208">
          <CardHeader data-id="element-3209">
            <CardTitle className="flex items-center gap-2" data-id="element-3210">
              <ShoppingBag className="w-5 h-5 text-gray-400" data-id="element-3211" />
              Cancelados Recientes
            </CardTitle>
          </CardHeader>
          <CardContent data-id="element-3212">
            {ordersLoading ? <div className="space-y-4" data-id="element-3213">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 w-full" data-id="element-3214" />)}
              </div> : orders.length > 0 ? <div className="overflow-x-auto" data-id="element-3215">
                <table className="w-full text-sm text-left" data-id="element-3216">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-3217">
                    <tr data-id="element-3218">
                      <th className="px-4 py-3 rounded-l-lg" data-id="element-3219">Pedido</th>
                      <th className="px-4 py-3" data-id="element-3220">Cliente</th>
                      <th className="px-4 py-3" data-id="element-3221">Teléfono</th>
                      <th className="px-4 py-3" data-id="element-3222">Total</th>
                      <th className="px-4 py-3" data-id="element-3223">Artículos</th>
                      <th className="px-4 py-3" data-id="element-3224">Estado</th>
                      <th className="px-4 py-3 rounded-r-lg" data-id="element-3225">Fecha</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3226">
                    {orders.map(order => <tr key={order.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors" data-id="element-3227">
                        <td className="px-4 py-3" data-id="element-3228">
                          <span className="font-medium text-gray-900" data-id="element-3229">
                            #{order.order_number}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700" data-id="element-3230">
                          {order.customer_name || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-500" data-id="element-3231">
                          {order.customer_phone || '—'}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900" data-id="element-3232">
                          {formatCurrency(order.total)}
                        </td>
                        <td className="px-4 py-3 text-gray-500" data-id="element-3233">
                          {order.items.length}{' '}
                          {order.items.length === 1 ? 'artículo' : 'artículos'}
                        </td>
                        <td className="px-4 py-3" data-id="element-3234">
                          <Badge variant="danger" data-id="element-3235">Cancelado</Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-500" data-id="element-3236">
                          <div className="text-sm" data-id="element-3237">
                            {new Date(order.created_at).toLocaleDateString('es-CR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-3238">
                            {formatRelativeTime(order.created_at)}
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div> : <div className="text-center py-12" data-id="element-3239">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3240">
                  <XCircle className="w-8 h-8 text-gray-300" data-id="element-3241" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-1" data-id="element-3242">
                  Sin pedidos cancelados
                </h3>
                <p className="text-sm text-gray-400" data-id="element-3243">
                  No hay pedidos cancelados registrados.
                </p>
              </div>}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>;
}